import { STAGE_MAP, fmtMoney, INVOICE_STATUS } from "@/lib/constants";

export const StatusBadge = ({ status, className = "" }) => {
  const s = STAGE_MAP[status];
  if (!s) return null;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${s.badge} ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

export const InvoiceStatusBadge = ({ status, overdue }) => {
  const s = INVOICE_STATUS[status] || INVOICE_STATUS.sent;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>
      {overdue && <span className="px-2 py-0.5 rounded text-xs font-medium bg-rose-600 text-white">Overdue</span>}
    </span>
  );
};

export const SampleTag = ({ show }) =>
  show ? (
    <span data-testid="sample-tag" className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
      Sample
    </span>
  ) : null;

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2 min-w-0">{children}</div>}
  </div>
);

export const Card = ({ title, action, children, className = "", testid }) => (
  <section data-testid={testid} className={`bg-white border border-slate-200 rounded-lg ${className}`}>
    {(title || action) && (
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {action}
      </header>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Field = ({ label, hint, children, required }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-600 mb-1.5">
      {label} {required && <span className="text-rose-500">*</span>}
    </span>
    {children}
    {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
  </label>
);

export const EmptyState = ({ title, hint, testid, action }) => (
  <div data-testid={testid} className="text-center py-10 text-slate-500">
    <div className="text-sm font-medium text-slate-600">{title}</div>
    {hint && <div className="text-xs mt-1 max-w-lg mx-auto">{hint}</div>}
    {action && <div className="mt-4 flex justify-center">{action}</div>}
  </div>
);

export const MoneyList = ({ items, size = "lg", testid }) => {
  if (!items || items.length === 0) return <div className={`${size === "lg" ? "text-2xl" : "text-base"} font-semibold text-slate-400`} data-testid={testid}>—</div>;
  return (
    <div className="space-y-0.5" data-testid={testid}>
      {items.map((m) => (
        <div key={m.currency} className={`${size === "lg" ? "text-2xl" : "text-base"} font-heading font-bold text-slate-900 tabular-nums leading-tight`}>
          {fmtMoney(m.amount, m.currency)}
        </div>
      ))}
    </div>
  );
};

export const KpiCard = ({ title, value, sub, testid, tone = "slate" }) => {
  const tones = { slate: "border-slate-200", teal: "border-teal-300", emerald: "border-emerald-300", amber: "border-amber-300", rose: "border-rose-300" };
  return (
    <div data-testid={testid} className={`bg-white border rounded-lg p-5 border-l-4 ${tones[tone]} fade-up`}>
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{title}</div>
      <div className="mt-2">{typeof value === "object" ? value : <div className="text-2xl font-heading font-bold text-slate-900 tabular-nums">{value}</div>}</div>
      {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
    </div>
  );
};

export const InfoRow = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
    <span className="text-xs text-slate-500 shrink-0 pt-0.5">{label}</span>
    <span className="text-sm text-slate-800 text-right">{children}</span>
  </div>
);
